import {
  Button,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import { useDiscountCode, useTableDiscountCode } from "../hooks";
import { TableColumns } from "../../../shared/components/ui/Table/types";
import { DataDiscountCode } from "../../../redux/@types";
import Table from "../../../shared/components/ui/Table";
import { discountsService } from "../../../shared/services";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (value: Date | string) => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDiscountLabel = (item: DataDiscountCode) => {
  if (item.value_amount) {
    return { label: formatCurrency(Number(item.value_amount)), color: "green" as const };
  }
  if (item.value_percentage) {
    return { label: `${item.value_percentage}%`, color: "blue" as const };
  }
  return { label: "Sin valor", color: "default" as const };
};

export const TableDiscountCode = () => {
  const { changeDiscountCodeForUpdate, handleDelete } = useTableDiscountCode();
  const { setTableProps, updateStatus, isLoading } = useDiscountCode();

  const columns: TableColumns<DataDiscountCode> = [
    {
      title: "Cupón",
      dataIndex: "name",
      key: "name",
      filterType: "text",
      render: (name: string, item) => (
        <div>
          <strong>{name}</strong>
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
            Código: <Tag style={{ margin: 0 }}>{item.code}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Descuento",
      key: "discount_value",
      render: (_, item) => {
        const discount = getDiscountLabel(item);
        return <Tag color={discount.color}>{discount.label}</Tag>;
      },
    },
    {
      title: "Disponibilidad",
      key: "availability",
      render: (_, item) => (
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <div>
            <span style={{ color: "#8c8c8c" }}>Total: </span>
            <strong>{item.quantity ?? 0}</strong>
          </div>
          <div>
            <span style={{ color: "#8c8c8c" }}>Por usuario: </span>
            <strong>{item.uses_by_user ?? 0}</strong>
          </div>
        </div>
      ),
    },
    {
      title: "Compra mínima",
      dataIndex: "order_min_amount",
      key: "order_min_amount",
      render: (value: number) =>
        value > 0 ? formatCurrency(Number(value)) : <span style={{ color: "#999" }}>Sin mínimo</span>,
    },
    {
      title: "Vigencia",
      key: "dates",
      render: (_, item) => (
        <span style={{ fontSize: 12 }}>
          {formatDate(item.date_start)} — {formatDate(item.date_end)}
        </span>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Activo", value: "active" },
        { text: "Inactivo", value: "inactive" },
      ],
      render: (status: string, item) => (
        <Switch
          checked={status === "active"}
          loading={isLoading}
          onChange={(checked) =>
            updateStatus(checked ? "active" : "inactive", item.id)
          }
          checkedChildren="Activo"
          unCheckedChildren="Inactivo"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, item) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => changeDiscountCodeForUpdate(item)}
            />
          </Tooltip>
          <Popconfirm
            title={`¿Eliminar el cupón "${item.name}"?`}
            onConfirm={() => handleDelete(item.id, item.name)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okType="danger"
          >
            <Tooltip title="Eliminar">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table<DataDiscountCode>
      columns={columns}
      fetchQueryProps={{
        $sort: { id: -1 },
      }}
      service={discountsService}
      onLoad={setTableProps}
    />
  );
};
